import { useEffect, useMemo, type VoidFunctionComponent } from 'react';
import type { InputActionMeta } from 'react-select';

import { useSearchPackageArchitectures } from '../../../hooks/api/registrySearch/useSearchPackageArchitectures';
import FormLabel from '../../generic/Forms/FormLabel';
import { Select } from '../../generic/Select';
import type { ThirdPartyFetcher } from '../../../../shared/fetchers';
import type { ArchitectureHit } from '../../../../shared/registrySearch';

import { NO_DROPDOWN_COMPONENTS } from './selectIndicators';

import styles from './PackageAutocomplete.module.less';

type ArchitectureOption = {
  value: string;
  label: string;
};

// Extracted to module scope (instead of inlining in JSX) to avoid `react/no-unstable-nested-components`.
const formatOptionLabel = (opt: ArchitectureOption) => (
  <div className={styles.optionRow}>
    <div className={styles.optionLabel}>{opt.label}</div>
  </div>
);

type Props = {
  id: string;
  fetcher: ThirdPartyFetcher;
  packageName: string;
  packageVersion: string;
  value: string;
  /**
   * Emitted with the selected architecture and a `fromTypeahead` flag indicating whether the value
   * came from the prepopulated list (true) or was typed/created by the user (false).
   */
  onChange: (architecture: string, fromTypeahead: boolean) => void;
  /**
   * Fired whenever an architecture list finishes loading for the current package/version, with
   * the raw architecture strings. The parent uses this to revalidate an arch preserved across a
   * package or version change (keeping it only if it still exists in the new list)
   */
  onArchitecturesLoaded?: (architectures: string[]) => void;
  /**
   * Whether the typeahead dropdown should engage. When false, the field still accepts typed
   * input (manual entry) but skips the architecture-list fetch and keeps the dropdown closed —
   * used when the package shape isn't one our backend can autocomplete.
   */
  isMenuEnabled?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
};

export const PackageArchitectureAutocomplete: VoidFunctionComponent<Props> = ({
  id, fetcher, packageName, packageVersion, value, onChange, onArchitecturesLoaded,
  isMenuEnabled = true, isDisabled, isRequired,
}) => {
  const { data, isLoading } = useSearchPackageArchitectures(
    fetcher,
    packageName,
    packageVersion,
    isMenuEnabled,
  );

  // `data` is never a previous package/version's list here, so on a key change it goes undefined until the new list arrives.
  useEffect(() => {
    if (data?.results) {
      onArchitecturesLoaded?.(data.results.map((hit) => hit.value));
    }
  }, [data, onArchitecturesLoaded]);

  const options = useMemo<ArchitectureOption[]>(() => {
    const hits: ArchitectureHit[] = data?.results ?? [];
    return hits.map((hit) => ({
      value: hit.value,
      label: hit.value,
    }));
  }, [data]);

  return (
    <FormLabel label="Architecture" htmlFor={`${id}-input`} requiredText={isRequired ? '(Required)' : undefined}>
      <Select<ArchitectureOption, false>
        id={id}
        inputId={`${id}-input`}
        className={styles.alwaysVisibleInput}
        // Keep react-select's internal selected-option state always null. We use this as a
        // controlled-input typeahead (we own `inputValue` and bubble picks via `onChange`); we
        // never read react-select's `value` ourselves, so letting it accumulate a stale
        // selection would only be a source of subtle bugs (e.g. previously-picked option
        // re-highlighting on menu reopen, or keyboard nav landing on a stale row).
        value={null}
        // Controlled-input mode (matches PackageNameAutocomplete) — renders the selected/typed
        // version as plain editable text, not a non-editable chip.
        inputValue={value}
        controlShouldRenderValue={false}
        // Force the menu closed in manual-entry mode (unsupported package shape) and strip the
        // dropdown indicators so the field reads as a plain input.
        menuIsOpen={isMenuEnabled ? undefined : false}
        components={isMenuEnabled ? undefined : NO_DROPDOWN_COMPONENTS}
        options={options}
        onInputChange={(input: string, meta: InputActionMeta) => {
          if (meta.action === 'input-change') {
            onChange(input, false);
          }
        }}
        onChange={(opt) => {
          if (opt) {
            onChange(opt.value, true);
          }
        }}
        formatOptionLabel={formatOptionLabel}
        placeholder={packageName && packageVersion ? 'Select an architecture' : 'Pick a package and version first'}
        isDisabled={isDisabled || !packageName || !packageVersion}
        // Gate the spinner on `isMenuEnabled` — react-query's `isLoading` can stay truthy
        // briefly during queryKey transitions even when `enabled` flips false, which would
        // show a misleading throbber while typing a value our backend won't search.
        isLoading={isMenuEnabled && isLoading}
        noOptionsMessage={() => (packageName && packageVersion ? 'No architectures found' : 'Pick a package and version first')}
      />
    </FormLabel>
  );
};
